import React from 'react';
import * as Icons from '@mui/icons-material';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from '../../../components/Link';
import { DataTable } from '../../../components/DataTable';
import * as Entities from '../../../constants/Entities';
import { entityLocation } from '../../../utils/entity';
import { TooltipIcon } from '../../../components/TooltipIcon';

interface Unit {
  id: string;
  name?: string | null;
  unitSymbol?: string | null;
  isGlobal?: boolean | null;
  deviceModelCapabilities?: Array<unknown> | null;
}

interface UnitListProps {
  units: Unit[];
  onAdd?: () => void;
  onChangePage: (page: number) => void;
  onChangePageSize: (pageSize: number) => void;
  onSearchSubmit: (query: string) => void;
  searchValue: string;
  page: number;
  pageSize: number;
  count: number;
  loading: boolean;
}

export const UnitList: React.FC<UnitListProps> = ({
  units,
  onAdd,
  onChangePageSize,
  onChangePage,
  page,
  pageSize,
  count,
  onSearchSubmit,
  searchValue,
  loading,
}) => {
  const { t } = useTranslation(['units', 'general']);

  const headers = [
    {
      key: 'name',
      value: t('units:list.headers.name'),
    },
    {
      key: 'capabilityCount',
      value: t('units:list.headers.capabilityCount'),
    },
    {
      key: 'unitSymbol',
      value: t('units:list.headers.symbol'),
    },
    {
      key: 'unitType',
      value: (
        <TooltipIcon
          text={t('units:list.headers.unitType')}
          tootltipText={t('units:list.unitTypeTooltip')}
        />
      ),
    },
    {
      key: 'actions',
      value: '',
    },
  ];

  const rows = units.map((unit) => ({
    key: `${unit.id}-row`,
    cells: [
      {
        key: `${unit.id}-cell-name`,
        element: (
          <Link to={entityLocation(Entities.UNIT, unit.id)}>
            {unit.name || unit.id}
          </Link>
        ),
      },
      {
        key: `${unit.id}-cell-unitCount`,
        element: unit.deviceModelCapabilities?.length ?? 0,
      },
      {
        key: `${unit.id}-cell-unitSymbol`,
        element: unit.unitSymbol,
      },
      {
        key: `${unit.id}-cell-unitType`,
        element: unit.isGlobal
          ? t('general:entityType.global')
          : t('general:entityType.local'),
      },
      {
        key: `${unit.id}-cell-actions`,
        element: null,
      },
    ],
  }));

  return (
    <DataTable
      actions={
        onAdd && (
          <Button
            color="secondary"
            variant="outlined"
            size="large"
            startIcon={<Icons.Add />}
            onClick={onAdd}
          >
            {t('units:list.actions.addButton')}
          </Button>
        )
      }
      showSearch
      page={page}
      loading={loading}
      pageSize={pageSize}
      count={count}
      onChangePage={onChangePage}
      onChangePageSize={onChangePageSize}
      onSearchSubmit={onSearchSubmit}
      searchValue={searchValue}
      headers={headers}
      rows={rows}
      showPagination
    />
  );
};
